/**
 * Harry LLM service — conversational routing on top of the capability catalog.
 */

import { AgentLlmError, callAgentLlm } from './llmClient';
import type { HarryVoiceMode } from './harry/harryPrompts';
import {
  defaultHarrySystemPrompt,
  packHarrySettings,
  unpackHarrySettings,
  isDefaultHarryPromptBody,
  HARRY_CAPABILITY_APPENDIX,
  HARRY_ISM_SPIN_APPENDIX,
} from './harry/harryPrompts';
import type { AgentSettings } from './agentSettingsService';
import { buildInstructionStack } from './ai/instructionStack';
import { HARRY_CARD } from './ai/agentCard';
import { serializePacket, type SurfacePacket } from './ai/contextPacket';
import { DEFAULT_MODEL } from '../../supabase/functions/_shared/modelRequest.ts';

export interface HarryLlmResult {
  reply: string;
  capabilityIds: string[];
}

function parseHarryJson(raw: string): HarryLlmResult | null {
  const trimmed = raw.trim();
  const jsonBlock = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/)?.[1] ?? trimmed;
  try {
    const parsed = JSON.parse(jsonBlock) as { reply?: unknown; capabilityIds?: unknown };
    const reply = typeof parsed.reply === 'string' ? parsed.reply.trim() : '';
    const capabilityIds = Array.isArray(parsed.capabilityIds)
      ? parsed.capabilityIds.filter((id): id is string => typeof id === 'string')
      : [];
    if (!reply) return null;
    return { reply, capabilityIds };
  } catch {
    return null;
  }
}

export function resolveHarrySystemPrompt(config: AgentSettings | null, voiceMode: HarryVoiceMode): string {
  if (!config?.systemPrompt?.trim()) {
    return defaultHarrySystemPrompt(voiceMode);
  }
  const { promptBody } = unpackHarrySettings(config.systemPrompt);
  return promptBody || defaultHarrySystemPrompt(voiceMode);
}

export function resolveHarryVoiceMode(config: AgentSettings | null): HarryVoiceMode {
  if (!config?.systemPrompt?.trim()) return 'normal';
  return unpackHarrySettings(config.systemPrompt).voiceMode;
}

/**
 * Canon-aware system prompt (Slice 1, behind the `canon_aware_harry` module +
 * flag pair): the layered instruction stack replaces the flat prompt. A
 * workspace-customized prompt body rides along as the subordinate overlay —
 * it can specialize tone, never rights (rights live in L0/L1 and in the
 * absence of tools).
 */
export function buildCanonAwareHarrySystemPrompt(
  config: AgentSettings | null,
  voiceMode: HarryVoiceMode,
  uiMode: 'b' | 'c' | null,
): string {
  const storedBody = config?.systemPrompt ? unpackHarrySettings(config.systemPrompt).promptBody : '';
  const overlay = isDefaultHarryPromptBody(storedBody) ? null : storedBody;
  return buildInstructionStack({
    card: HARRY_CARD,
    voiceMode,
    uiMode,
    workspaceOverlay: overlay,
    capabilityAppendix: HARRY_CAPABILITY_APPENDIX,
  }).systemPrompt;
}

export async function askHarry(params: {
  config: AgentSettings;
  voiceMode: HarryVoiceMode;
  userPrompt: string;
  workspaceId: string;
  /** Canon-Aware Harry (module `canon_aware_harry`): layered stack + packet. */
  canonAware?: boolean;
  packet?: SurfacePacket | null;
  uiMode?: 'b' | 'c' | null;
}): Promise<HarryLlmResult> {
  const systemPrompt = params.canonAware
    ? buildCanonAwareHarrySystemPrompt(params.config, params.voiceMode, params.uiMode ?? null)
    : resolveHarrySystemPrompt(params.config, params.voiceMode);
  const userPrompt = params.canonAware && params.packet
    ? `${serializePacket(params.packet)}\n\n${params.userPrompt}`
    : params.userPrompt;
  const model = params.config.model?.trim() || DEFAULT_MODEL;

  const content = await callAgentLlm({
    userPrompt,
    systemPrompt,
    model,
    conversationId: `harry-${params.workspaceId}`,
    maxTokens: 900,
    temperature: params.voiceMode === 'harry' ? 0.65 : 0.35,
  });

  const parsed = parseHarryJson(content);
  if (parsed) return parsed;

  return {
    reply: content.trim(),
    capabilityIds: [],
  };
}

function parseHarryIsm(raw: string): string | null {
  const trimmed = raw.trim();
  const jsonBlock = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/)?.[1] ?? trimmed;
  try {
    const parsed = JSON.parse(jsonBlock) as { ism?: unknown };
    if (typeof parsed.ism === 'string' && parsed.ism.trim()) return parsed.ism.trim();
  } catch { /* fall through */ }
  if (trimmed.length > 0 && trimmed.length < 600) return trimmed;
  return null;
}

/** Spin the wheel — one contextual Harry-ism from quotes in the workspace system prompt. */
export async function spinHarryism(params: {
  config: AgentSettings;
  userPrompt: string;
  workspaceId: string;
}): Promise<string> {
  const { promptBody } = unpackHarrySettings(params.config.systemPrompt);
  const base = promptBody || defaultHarrySystemPrompt('harry');
  const systemPrompt = `${base}\n\n${HARRY_ISM_SPIN_APPENDIX}`;
  const model = params.config.model?.trim() || DEFAULT_MODEL;

  const content = await callAgentLlm({
    userPrompt: params.userPrompt,
    systemPrompt,
    model,
    conversationId: `harry-ism-${params.workspaceId}-${Date.now()}`,
    maxTokens: 200,
    temperature: 0.85,
  });

  return parseHarryIsm(content) ?? content.trim();
}

export { packHarrySettings, unpackHarrySettings, defaultHarrySystemPrompt };
export type { HarryVoiceMode };

export { AgentLlmError };
